// HANDLER: Block
// Here we first create a type that matches the Entity to a particular type of block
// Notice that the keys of this interface are not enforced
interface EntityBlockMap {
  match: MatchBlock[];
  tournament: TournamentBlock[];
  series: SeriesBlock[];
}

// Then we use the CompleteRecord helper to 'link' our Map to the Entity union type
// This will ensure all possible values from a union type are accounted for, while keeping a specific type for each key
// If we add an entity, an error will pop here to say that EntityBlockMap is missing a key
const AVAILABLE_BLOCK_SLUGS: CompleteRecord<Entity, EntityBlockMap> = {
  match: [
    'name',
    'head-to-head',
    'form-guide',
    'rosters',
    'teams-stats',
    'players-stats',
    'stream',
    'series-tournaments',
    'series-matches',
    'series-stats',
    'series-overview'
  ],
  tournament: ['bracket'],
  series: ['tournaments']
};

interface EntityFullBlocksMap {
  match: {
    full_widget: MatchBlock[];
    detailed_only_blocks: MatchBlock[];
  };
  tournament: {
    full_widget: TournamentBlock[];
    detailed_only_blocks: TournamentBlock[];
  };
  series: {
    full_widget: SeriesBlock[];
    detailed_only_blocks: SeriesBlock[];
  };
}

const ENTITY_BLOCKS_CONFIG: CompleteRecord<Entity, EntityFullBlocksMap> = {
  match: {
    full_widget: ['name', 'head-to-head', 'form-guide', 'teams-stats', 'players-stats'],
    detailed_only_blocks: ['teams-stats', 'pool', 'players-stats', 'players-head-to-head']
  },
  tournament: {
    full_widget: ['bracket'],
    detailed_only_blocks: []
  },
  series: {
    full_widget: ['tournaments'],
    detailed_only_blocks: []
  }
};

// Specify blocks that are not in the API and for which the data comes from another block
const BLOCK_QUERY_MAP: Partial<Record<Block, Block>> = {
  'players-head-to-head': 'players-stats',
  pool: 'teams-stats',
  'series-matches': 'series-overview' // allow to fetch series ID. Matches fetching is done in the corresponding block directly
};

const TABS_CONTENT: Record<Tab, Block[]> = {
  teams: ['teams-stats', 'pool', 'form-guide', 'head-to-head'],
  players: ['players-stats', 'players-head-to-head'],
  tournament: ['series-tournaments', 'series-matches', 'series-stats', 'series-overview']
};

const BLOCKS_WITH_PERIOD: Block[] = ['players-stats', 'teams-stats'];

// HANDLER: Entity
const ENTITIES: Entity[] = ['match', 'tournament', 'series'];

const PERIODS: StatPeriod[] = ['3-month', 'series'];

const PERIODS_URL: Record<StatPeriod, string> = {
  '3-month': 'last_3_months',
  series: 'serie'
};

const SCOREBOARD_TYPES: ScoreboardType[] = ['basic', 'detailed', 'minimap'];

const VIDEOGAMES_FOR_SCOREBOARD: Record<ScoreboardType, VideogameSlug[]> = {
  basic: [], // No specific videogames for basic scoreboard
  detailed: [], // No specific videogames for detailed scoreboard
  minimap: ['dota-2']
};

const DEFAULT_VALUES: Pick<
  UrlParams,
  | 'block'
  | 'tab'
  | 'lang'
  | 'period'
  | 'period_edit'
  | 'full_height'
  | 'multi_lang'
  | 'scoreboard'
  | 'title'
> & { theme: RegisteredThemes; designVersion: DesignVersion } = {
  block: [],
  tab: [],
  lang: 'en-US',
  period: '3-month',
  period_edit: false,
  full_height: false,
  theme: 'default',
  designVersion: 'default',
  multi_lang: false,
  scoreboard: 'basic',
  title: true
};

const DESIGN_VERSION_FOR_CUSTOMER: Record<string, DesignVersion> = {
  pariru: 'grad'
};

export {
  AVAILABLE_BLOCK_SLUGS,
  ENTITY_BLOCKS_CONFIG,
  ENTITIES,
  DEFAULT_VALUES,
  PERIODS,
  PERIODS_URL,
  BLOCKS_WITH_PERIOD,
  SCOREBOARD_TYPES,
  VIDEOGAMES_FOR_SCOREBOARD,
  TABS_CONTENT,
  DESIGN_VERSION_FOR_CUSTOMER,
  BLOCK_QUERY_MAP
};
